/**
 * LOS REPORTES DE JUGADOR DENTRO DEL CLUB.
 *
 * Son DOS COSAS EN UN ARCHIVO y a propósito: el formulario que escribe uno y la
 * lista que los lee. Son las dos caras de la misma regla —lo escribe cualquier
 * miembro, lo lee solo quien administra— y separarlas en dos archivos invita a
 * que un día alguien cambie una y se olvide de la otra.
 *
 * QUIEN NO ADMINISTRA NO VE LA LISTA, NI SIQUIERA LOS SUYOS. No es por
 * esconderlo: un tablón de acusaciones visible hace que nadie reporte a alguien
 * con quien va a tener que jugar el sábado. El servidor lo rechaza igual; acá
 * ni se dibuja, porque un botón que lleva a un 403 es peor que no tener botón.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACCEPTED_IMAGE_MIME_TYPES,
  IMAGE_MAX_UPLOAD_BYTES,
  PLAYER_REPORT_LIMITS,
  type CommunityMember,
  type PlayerReport,
  type PlayerReportPage,
  type PlayerReportQuery,
  type PlayerReportSummaryOptions,
} from '@tennisup/contracts';

import { Alert } from './Alert';
import { Avatar } from './Avatar';
import { api, assetUrl } from '../lib/api';
import { usePlayerSearch, MIN_SEARCH_LENGTH } from '../hooks/usePlayerSearch';
import { errorMessage } from '../lib/errorMessage';

/** «YYYY-MM-DD» de hoy en la zona de quien mira. Es el tope del campo. */
function hoy(): string {
  const ahora = new Date();
  return new Date(ahora.getTime() - ahora.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10);
}

/**
 * REPORTAR A ALGUIEN DEL CLUB.
 *
 * VIENE PLEGADO. Es de las cosas que se usan tres veces al año, y un formulario
 * de tres campos siempre abierto en la pantalla del club le da a un problema
 * excepcional el mismo peso visual que a la lista de jugadores.
 */
export function ReportPlayerBox({
  communityId,
  members,
  meId,
}: {
  communityId: string;
  members: CommunityMember[];
  meId: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [abierto, setAbierto] = useState(false);
  /*
   * A QUIÉN SE ELIGIÓ: el id Y EL NOMBRE, no solo el id.
   *
   * Con el desplegable bastaba el id —el nombre estaba en la lista de al
   * lado—. Con un buscador, la lista desaparece al elegir, así que si solo se
   * guardara el id la pantalla no tendría con qué decir a quién se eligió y
   * habría que ir a buscarlo a `members`, que es justamente lo que este
   * cambio deja de necesitar.
   */
  const [elegido, setElegido] = useState<{ userId: string; displayName: string } | null>(null);
  const [query, setQuery] = useState('');
  const [cuando, setCuando] = useState(hoy());
  const [problema, setProblema] = useState('');
  const [fotos, setFotos] = useState<File[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);

  /*
   * `members` YA NO ALIMENTA EL CAMPO: sirve para UNA sola pregunta, «¿hay
   * alguien más en este club?».
   *
   * Antes llenaba el desplegable. Desde que se escribe el nombre, a quién se
   * puede reportar lo contesta el servidor, y esta lista quedó solo para no
   * ofrecer un formulario imposible en un club de una persona. Se deja porque
   * la pantalla ya la tiene en la mano: preguntárselo al servidor sería una
   * consulta para saber algo que acá ya se sabe.
   *
   * `Array.isArray` porque `CommunityMember[]` es una promesa sobre el
   * contrato, no una garantía sobre el cable.
   */
  const otros = (Array.isArray(members) ? members : []).filter((m) => m.userId !== meId);

  const busqueda = usePlayerSearch(communityId, query);

  /*
   * A UNO MISMO NO SE REPORTA, y el buscador no lo sabe: devuelve a cualquiera
   * del club. El desplegable de antes filtraba `members`; acá se filtra el
   * resultado, que es la misma regla en el lugar que ahora corresponde.
   */
  const candidatos = busqueda.results.filter((p) => p.userId !== meId);

  const puedeMandar =
    elegido !== null &&
    cuando !== '' &&
    problema.trim().length >= PLAYER_REPORT_LIMITS.problemMin &&
    !enviando;

  function elegirFotos(lista: FileList | null): void {
    if (lista === null) return;
    const elegidas = [...lista];

    // La comprobación de acá es de conveniencia, no de seguridad: sirve para
    // decirlo en el acto en vez de que suba ocho megas y le rebote. Quien
    // decide de verdad es el servidor, que mira los bytes.
    if (elegidas.length > PLAYER_REPORT_LIMITS.photosMax) {
      setError(t('reports.tooManyPhotos', { max: PLAYER_REPORT_LIMITS.photosMax }));
      return;
    }
    if (elegidas.some((f) => !ACCEPTED_IMAGE_MIME_TYPES.some((tipo) => f.type === tipo))) {
      setError(t('image.wrongType'));
      return;
    }
    if (elegidas.some((f) => f.size > IMAGE_MAX_UPLOAD_BYTES)) {
      setError(t('image.tooBig', { megabytes: Math.floor(IMAGE_MAX_UPLOAD_BYTES / (1024 * 1024)) }));
      return;
    }
    setError(null);
    setFotos(elegidas);
  }

  async function mandar(): Promise<void> {
    // `elegido` se comprueba aparte del `puedeMandar` para que TypeScript
    // sepa que no es nulo más abajo: el booleano no le dice nada.
    if (!puedeMandar || elegido === null) return;
    setEnviando(true);
    setError(null);
    try {
      await api.createPlayerReport(
        communityId,
        { aboutUserId: elegido.userId, happenedOn: cuando, problem: problema.trim() },
        fotos,
      );
      // SE CIERRA Y SE DICE QUE LLEGÓ. Un formulario que se vacía sin decir
      // nada se lee como que falló, y la persona lo manda otra vez.
      setListo(true);
      setAbierto(false);
      setElegido(null);
      setQuery('');
      setProblema('');
      setFotos([]);
      setCuando(hoy());
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setEnviando(false);
    }
  }

  // Sin nadie a quien reportar no hay nada que ofrecer: un club de una persona.
  if (otros.length === 0) return <></>;

  if (!abierto) {
    return (
      <section className="report-box">
        {listo && <Alert kind="success">{t('reports.sent')}</Alert>}
        <p className="button-row">
          <button type="button" className="link" onClick={() => setAbierto(true)}>
            {t('reports.open')}
          </button>
        </p>
      </section>
    );
  }

  return (
    <section className="report-box">
      <h2>{t('reports.title')}</h2>
      {/* QUIÉN LO VA A LEER SE DICE ANTES DE ESCRIBIR, no después. Quien no
          sabe a dónde va lo que escribe, o no escribe o escribe de más. */}
      <p className="help">{t('reports.who')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {/*
        SE ESCRIBE EL NOMBRE, NO SE ELIGE DE UNA LISTA.

        Javier: «es mejor escribir apodo o nombre que un drop list porque puede
        haber mil personas en un futuro».

        Y no es solo el tamaño: un desplegable con doscientos nombres obliga a
        RECONOCER —desplazarse hasta encontrar el que uno ya tiene en la
        cabeza— cuando lo que la persona quiere es RECORDAR: sabe a quién va a
        reportar antes de abrir el formulario. Escribir tres letras llega antes
        que desplazarse, y llega igual de rápido con diez personas que con mil.

        EL MISMO BUSCADOR QUE USA INSCRIBIR GENTE EN UN TORNEO: busca contra el
        servidor, espera a que uno deje de escribir y no se baja la tabla
        entera con la primera letra.
      */}
      <div className="field">
        <label htmlFor="report-about">{t('reports.about')}</label>

        {elegido === null ? (
          <>
            <input
              id="report-about"
              type="search"
              autoComplete="off"
              value={query}
              disabled={enviando}
              placeholder={t('reports.searchPlaceholder')}
              onChange={(e) => setQuery(e.target.value)}
            />

            {/* EL «SEGUÍ ESCRIBIENDO» LO DICE EL HOOK y no un `length > 2` de
                acá: es la misma regla que decide si se busca, y escrita dos
                veces un día dicen cosas distintas. */}
            {busqueda.tooShort && (
              <span className="help">{t('reports.keepTyping', { min: MIN_SEARCH_LENGTH })}</span>
            )}

            {candidatos.length > 0 && (
              <ul className="member-list report-search">
                {candidatos.map((player) => (
                  <li key={player.userId}>
                    <Avatar url={assetUrl(player.avatarPath)} name={player.displayName} size={32} />
                    <div className="member-name">
                      <strong>{player.displayName}</strong>
                      {/* EL NOMBRE LEGAL DEBAJO DEL APODO: en un club hay dos
                          Marcos, y el apodo solo no alcanza para saber a cuál
                          se está por reportar. */}
                      <span className="help">{player.fullName}</span>
                    </div>
                    <button
                      type="button"
                      disabled={enviando}
                      onClick={() => {
                        setElegido({ userId: player.userId, displayName: player.displayName });
                        setQuery('');
                      }}
                    >
                      {t('reports.choosePlayer')}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* NADA QUE MOSTRAR NO ES LO MISMO QUE NO HABER BUSCADO. Sin esto,
                buscar a alguien que no está en el club deja la pantalla igual
                que antes de escribir y parece que la búsqueda no anda. */}
            {!busqueda.searching &&
              !busqueda.tooShort &&
              query.trim().length >= MIN_SEARCH_LENGTH &&
              candidatos.length === 0 && <span className="help">{t('reports.noMatches')}</span>}
          </>
        ) : (
          /* YA ELEGIDO: el nombre y cómo cambiarlo. Sin el botón, corregir una
             equivocación obligaría a cerrar el formulario y perder el relato
             ya escrito. */
          <p className="report-chosen">
            <strong>{elegido.displayName}</strong>{' '}
            <button
              type="button"
              className="link"
              disabled={enviando}
              onClick={() => setElegido(null)}
            >
              {t('common.change')}
            </button>
          </p>
        )}
      </div>

      <div className="field">
        <label htmlFor="report-when">{t('reports.when')}</label>
        {/* `max` es hoy: lo de mañana no pasó, y el servidor lo rechaza igual.
            Atajarlo acá evita escribir todo el relato para que rebote. */}
        <input
          id="report-when"
          type="date"
          max={hoy()}
          value={cuando}
          disabled={enviando}
          onChange={(e) => setCuando(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="report-problem">{t('reports.problem')}</label>
        <textarea
          id="report-problem"
          rows={5}
          maxLength={PLAYER_REPORT_LIMITS.problemMax}
          placeholder={t('reports.problemPlaceholder')}
          value={problema}
          disabled={enviando}
          onChange={(e) => setProblema(e.target.value)}
        />
        <p className="help">{t('reports.problemHelp')}</p>
      </div>

      <div className="field">
        <label htmlFor="report-photos">{t('reports.photos')}</label>
        <input
          id="report-photos"
          type="file"
          multiple
          accept={ACCEPTED_IMAGE_MIME_TYPES.join(',')}
          disabled={enviando}
          onChange={(e) => elegirFotos(e.target.files)}
        />
        <p className="help">{t('reports.photosHelp', { max: PLAYER_REPORT_LIMITS.photosMax })}</p>
        {fotos.length > 0 && (
          <p className="help">{t('reports.photosChosen', { count: fotos.length })}</p>
        )}
      </div>

      <p className="button-row">
        <button type="button" disabled={!puedeMandar} onClick={() => void mandar()}>
          {enviando ? t('common.sending') : t('reports.send')}
        </button>
        <button type="button" className="link" onClick={() => setAbierto(false)}>
          {t('common.cancel')}
        </button>
      </p>
    </section>
  );
}

/**
 * LA LISTA. Solo quien administra el club.
 *
 * SIN REPORTES NO SE DIBUJA NADA. Una sección vacía permanente en la pantalla
 * de quien administra es ruido que termina ignorándose, y el día que tenga algo
 * ya nadie la mira.
 */
export function ClubReports({ communityId }: { communityId: string }): JSX.Element | null {
  const { t, i18n } = useTranslation();

  /*
    PLEGADA DE ENTRADA. Antes la lista se desplegaba entera al abrir el club, y
    en un club con historia eso son cientos de acusaciones entre uno y el botón
    de torneos. Ahora es un botón que dice cuántas esperan; quien viene a otra
    cosa no las cruza, y quien viene a esto lo tiene a un toque.
  */
  const [abierto, setAbierto] = useState(false);

  const [pagina, setPagina] = useState<PlayerReportPage | null>(null);
  // `null` = todavía no se sabe si el club tiene reportes. Se separa de
  // `esperando` a propósito: ver el comentario del contador, abajo.
  const [hay, setHay] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);

  // Los filtros que la persona está tecleando, y los que están aplicados. Son
  // dos estados y no uno: con uno solo, cada tecla dispararía una consulta.
  const [about, setAbout] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [nro, setNro] = useState(1);

  /** Cuántas esperan, para el botón. Se pide aunque esté plegada. */
  const [esperando, setEsperando] = useState(0);

  const consulta = useMemo(
    (): PlayerReportQuery => ({ aboutUserId: about, from: desde, to: hasta, page: nro }),
    [about, desde, hasta, nro],
  );

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const datos = await api.listPlayerReports(communityId, consulta);
      setPagina(datos);
    } catch {
      // NO SE DICE NADA SI FALLA. Quien no administra recibe un 403 acá, y eso
      // no es un error que mostrarle: es la respuesta correcta.
      setPagina(null);
    } finally {
      setCargando(false);
    }
  }, [communityId, consulta]);

  // El contador del botón: una consulta sin filtro que solo mira el total de
  // los que esperan. Se hace aparte de `cargar` porque el filtro no lo cambia
  // —cuántas esperan en el club es una sola cosa, se esté viendo lo que se esté
  // viendo—.
  useEffect(() => {
    let vigente = true;
    void (async () => {
      try {
        const datos = await api.listPlayerReports(communityId, {});
        if (!vigente) return;
        setEsperando(datos.reports.filter((r) => r.handledAt === null).length);
        // HAY O NO HAY, que es otra cosa que «cuántas esperan». Un club donde
        // ya se miraron todas tiene cero esperando y SÍ tiene historia que
        // mostrar; decidir la sección por el contador la escondía entera.
        setHay(datos.total > 0);
        // Si nunca hubo ninguno, la sección no se dibuja: ver abajo.
        setPagina((antes) => antes ?? (datos.total === 0 ? datos : antes));
      } catch {
        if (vigente) {
          setEsperando(0);
          setHay(false);
        }
      }
    })();
    return () => {
      vigente = false;
    };
  }, [communityId]);

  useEffect(() => {
    if (abierto) void cargar();
  }, [abierto, cargar]);

  async function atender(id: string): Promise<void> {
    setOcupado(id);
    setError(null);
    try {
      const nuevo = await api.markPlayerReportHandled(communityId, id);
      setPagina((antes) =>
        antes === null
          ? antes
          : { ...antes, reports: antes.reports.map((r) => (r.id === nuevo.id ? nuevo : r)) },
      );
      setEsperando((n) => Math.max(0, n - 1));
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setOcupado(null);
    }
  }

  /** Cambiar un filtro vuelve a la página 1: la 4 del filtro viejo no existe. */
  function filtrar(cambio: () => void): void {
    cambio();
    setNro(1);
  }

  // SIN REPORTES NO SE DIBUJA NADA. Una sección vacía permanente en la pantalla
  // de quien administra es ruido que termina ignorándose, y el día que tenga
  // algo ya nadie la mira. `null` = todavía no se sabe.
  if (pagina !== null && pagina.total === 0 && about === '' && desde === '' && hasta === '') {
    return null;
  }
  // Mientras no se sepa, no se dibuja nada —no un esqueleto que parpadee—;
  // y si el club no tiene ni uno, tampoco. Pero si tiene, la sección va
  // AUNQUE no quede ninguna esperando.
  if (hay !== true && pagina === null) return null;

  return (
    <section className="club-reports">
      <div className="section-head">
        <button
          type="button"
          className="link"
          aria-expanded={abierto}
          onClick={() => setAbierto((x) => !x)}
        >
          {t('reports.listTitle')}
          {/* EL NÚMERO ES EL DE LOS QUE ESPERAN, no el total. El total no le
              pide nada a nadie; lo que espera, sí. */}
          {esperando > 0 && <span className="tag"> {esperando}</span>}
        </button>
      </div>

      {abierto && (
        <>
          <div className="report-filters">
            <label htmlFor="rep-about">
              {t('reports.filterPlayer')}
              <input
                id="rep-about"
                type="search"
                value={about}
                onChange={(e) => filtrar(() => setAbout(e.target.value))}
                placeholder={t('reports.filterPlayerPlaceholder')}
              />
            </label>
            <label htmlFor="rep-from">
              {t('reports.filterFrom')}
              <input
                id="rep-from"
                type="date"
                value={desde}
                onChange={(e) => filtrar(() => setDesde(e.target.value))}
              />
            </label>
            <label htmlFor="rep-to">
              {t('reports.filterTo')}
              <input
                id="rep-to"
                type="date"
                value={hasta}
                onChange={(e) => filtrar(() => setHasta(e.target.value))}
              />
            </label>
            {(about !== '' || desde !== '' || hasta !== '') && (
              <button
                type="button"
                className="link"
                onClick={() =>
                  filtrar(() => {
                    setAbout('');
                    setDesde('');
                    setHasta('');
                  })
                }
              >
                {t('reports.filterClear')}
              </button>
            )}
          </div>

          <p className="button-row">
            <ResumenImprimible communityId={communityId} consulta={consulta} />
          </p>

          {error !== null && <Alert kind="error">{error}</Alert>}

          {pagina !== null && pagina.total === 0 ? (
            <p className="help">{t('reports.noneMatch')}</p>
          ) : (
            <ul className="report-list" aria-busy={cargando}>
              {(pagina?.reports ?? []).map((reporte) => (
                <li
                  key={reporte.id}
                  className={reporte.handledAt === null ? 'report open' : 'report'}
                >
                  <div className="report-head">
                    <Avatar
                      url={assetUrl(reporte.aboutAvatarPath)}
                      name={reporte.aboutName}
                      size={36}
                    />
                    <div className="report-who">
                      <strong>{reporte.aboutName}</strong>
                      <span className="help">
                        {t('reports.by', {
                          name: reporte.reporterName,
                          date: díaCorto(reporte.happenedOn, i18n.language),
                        })}
                      </span>
                    </div>
                    {/* LA REINCIDENCIA, solo a partir del segundo. Un «1» en
                        cada fila es ruido: el dato es que hay más de uno. */}
                    {reporte.timesReported > 1 && (
                      <span className="tag tag-warn">
                        {t('reports.timesReported', { count: reporte.timesReported })}
                      </span>
                    )}
                  </div>

                  <p className="report-problem">{reporte.problem}</p>

                  {reporte.photoPaths.length > 0 && (
                    <div className="report-photos">
                      {reporte.photoPaths.map((ruta) => (
                        // Abre en pestaña nueva: una captura de WhatsApp es
                        // ilegible a 90 px, y quien revisa necesita leerla.
                        <a key={ruta} href={assetUrl(ruta) ?? ''} target="_blank" rel="noreferrer">
                          <img src={assetUrl(ruta) ?? ''} alt="" className="report-photo" />
                        </a>
                      ))}
                    </div>
                  )}

                  {reporte.handledAt === null ? (
                    <p className="button-row">
                      <button
                        type="button"
                        className="link"
                        disabled={ocupado === reporte.id}
                        onClick={() => void atender(reporte.id)}
                      >
                        {t('reports.markHandled')}
                      </button>
                    </p>
                  ) : (
                    <p className="help">
                      {t('reports.handledBy', { name: reporte.handledByName ?? '—' })}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {pagina !== null && pagina.pages > 1 && (
            <div className="report-pager">
              <button
                type="button"
                className="link"
                disabled={pagina.page <= 1 || cargando}
                onClick={() => setNro((n) => Math.max(1, n - 1))}
              >
                {t('reports.prev')}
              </button>
              <span className="help">
                {t('reports.pageOf', { page: pagina.page, pages: pagina.pages })}
              </span>
              <button
                type="button"
                className="link"
                disabled={pagina.page >= pagina.pages || cargando}
                onClick={() => setNro((n) => n + 1)}
              >
                {t('reports.next')}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * EL RESUMEN IMPRIMIBLE, con lo que se esconde elegido antes de generarlo.
 *
 * POR QUÉ SE IMPRIME Y NO SE DESCARGA UN PDF. El diálogo de impresión de todo
 * navegador ofrece «Guardar como PDF», así que el archivo sale igual, y en el
 * teléfono también. Meter una librería de PDF sería sumar dependencia,
 * duplicar la maquetación en código y perder el ajuste de página que el
 * navegador ya hace — para el mismo resultado.
 *
 * LOS TRES INTERRUPTORES SON INDEPENDIENTES porque el mismo papel sirve para
 * cosas distintas: mostrárselo a la persona reportada sin exponer a quien la
 * reportó, llevar un patrón a una junta sin señalar a nadie, o guardar el
 * respaldo entero.
 *
 * TRAE TODOS LOS QUE CUMPLEN EL FILTRO, no la página que se está viendo: un
 * resumen con los primeros diez es un resumen que miente sin avisar.
 */
function ResumenImprimible({
  communityId,
  consulta,
}: {
  communityId: string;
  consulta: PlayerReportQuery;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const [abierto, setAbierto] = useState(false);
  const [opciones, setOpciones] = useState<PlayerReportSummaryOptions>({
    hideReporter: true,
    hideAbout: false,
    hideDates: true,
  });
  const [filas, setFilas] = useState<PlayerReport[] | null>(null);
  const [pidiendo, setPidiendo] = useState(false);
  const [bajando, setBajando] = useState(false);

  async function preparar(): Promise<void> {
    setPidiendo(true);
    try {
      setFilas(await api.listPlayerReportsForSummary(communityId, consulta));
      setAbierto(true);
    } catch {
      setFilas(null);
    } finally {
      setPidiendo(false);
    }
  }

  function marcar(clave: keyof PlayerReportSummaryOptions): void {
    setOpciones((antes) => ({ ...antes, [clave]: !antes[clave] }));
  }

  /**
   * Baja el PDF.
   *
   * LOS TEXTOS SE LE PASAN AL GENERADOR en vez de que él los busque: así
   * `reportesPdf.ts` no depende de i18next ni del idioma, y se puede probar
   * pasándole textos de mentira. La pluralización queda acá, que es donde
   * está el catálogo que sabe hacerla.
   */
  async function bajar(): Promise<void> {
    if (filas === null) return;
    setBajando(true);
    try {
      const { descargarResumen } = await import('../lib/reportesPdf');
      descargarResumen(
        filas,
        opciones,
        {
          encabezado: t('reports.summaryHeading', { count: filas.length }),
          anonimo: (n) => t('reports.printedAnon', { n }),
          sobre: (nombre) => t('reports.printedAbout', { name: nombre }),
          reporto: (nombre) => t('reports.printedBy', { name: nombre }),
          veces: (n) => t('reports.timesReported', { count: n }),
          dia: (civil) => díaCorto(civil, i18n.language),
          pie: (pagina, total) => t('reports.pageOf', { page: pagina, pages: total }),
        },
        t('reports.listTitle'),
      );
    } finally {
      setBajando(false);
    }
  }

  return (
    <>
      <button type="button" className="link" disabled={pidiendo} onClick={() => void preparar()}>
        {t('reports.summary')}
      </button>

      {abierto && filas !== null && (
        <div className="report-summary">
          <div className="report-summary-options no-print">
            <h3>{t('reports.summaryTitle')}</h3>
            <label className="check">
              <input
                type="checkbox"
                checked={opciones.hideReporter}
                onChange={() => marcar('hideReporter')}
              />
              <span>{t('reports.hideReporter')}</span>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={opciones.hideAbout}
                onChange={() => marcar('hideAbout')}
              />
              <span>{t('reports.hideAbout')}</span>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={opciones.hideDates}
                onChange={() => marcar('hideDates')}
              />
              <span>{t('reports.hideDates')}</span>
            </label>

            <p className="button-row">
              <button type="button" disabled={bajando} onClick={() => void bajar()}>
                {t('reports.download')}
              </button>
              <button type="button" className="link" onClick={() => setAbierto(false)}>
                {t('common.cancel')}
              </button>
            </p>
            <p className="help">{t('reports.downloadHelp')}</p>
          </div>

          {/*
            LA VISTA PREVIA. Lo que se baja lo dibuja `reportesPdf.ts`, no
            esto: acá se ve ANTES de bajarlo, que es cuando todavía se puede
            cambiar un interruptor. Que sean dos maquetaciones es a propósito
            —la pantalla se ajusta al ancho que haya, la hoja tiene A4 fijo—.
          */}
          <div className="report-preview">
            <h2>{t('reports.summaryHeading', { count: filas.length })}</h2>

            {filas.map((r, i) => (
              <article key={r.id} className="report-printed">
                <h3>
                  {opciones.hideAbout
                    ? t('reports.printedAnon', { n: i + 1 })
                    : t('reports.printedAbout', { name: r.aboutName })}
                  {/* La reincidencia sobrevive al anonimato: es el patrón, y
                      es justo el dato por el que alguien imprime esto. */}
                  {r.timesReported > 1 && ` · ${t('reports.timesReported', { count: r.timesReported })}`}
                </h3>

                {(!opciones.hideReporter || !opciones.hideDates) && (
                  <p className="report-printed-meta">
                    {!opciones.hideReporter && t('reports.printedBy', { name: r.reporterName })}
                    {!opciones.hideReporter && !opciones.hideDates && ' · '}
                    {!opciones.hideDates && díaCorto(r.happenedOn, i18n.language)}
                  </p>
                )}

                <p>{r.problem}</p>
              </article>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * EL DÍA EN QUE PASÓ, en el idioma de la pantalla y SIN CORRERSE UN DÍA.
 *
 * `new Date('2026-09-19')` es la medianoche UTC; formateada con la zona de un
 * teléfono en Costa Rica —seis horas atrás— cae el 18. Por eso `timeZone: UTC`,
 * la misma razón que en `lib/monthYear.ts`.
 */
function díaCorto(civil: string, locale: string): string {
  const cuando = new Date(`${civil}T00:00:00Z`);
  if (Number.isNaN(cuando.getTime())) return civil;
  try {
    return cuando.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return civil;
  }
}
